import type { BootstrapSummary } from '@core/entities/BootstrapSummary';
import { BootstrapGatewayError } from '@core/ports/BootstrapGateway';
import { reconnectFromNhsAppGuidancePoints, restartFromNhsAppGuidancePoints, safeGuidanceHeading } from './safeGuidance';

export interface BootstrapCardViewModel {
  description: string;
  heading: string;
  pathPrefix: string;
}

export interface BootstrapReadyPageViewModel {
  kind: 'ready';
  cards: BootstrapCardViewModel[];
  intro: string;
  title: string;
}

export interface BootstrapFailurePageViewModel {
  kind: 'failure';
  guidanceHeading: string;
  guidancePoints: string[];
  message: string;
  title: string;
}

export type BootstrapPageViewModel = BootstrapReadyPageViewModel | BootstrapFailurePageViewModel;

export class BootstrapPresenter {
  public present(summary: BootstrapSummary): BootstrapReadyPageViewModel {
    return {
      kind: 'ready',
      title: `${summary.frontendAppName} foundation baseline`,
      intro: 'The frontend is hosted behind the BFF and exposes browser-safe entry points for downstream service slices.',
      cards: summary.downstreamServices.map((service) => ({
        heading: service.name.toUpperCase(),
        pathPrefix: service.pathPrefix,
        description: `Current-state browser-safe route prefix: ${service.pathPrefix}`
      }))
    };
  }

  public presentFailure(error: unknown): BootstrapFailurePageViewModel {
    if (error instanceof BootstrapGatewayError && error.code === 'offline') {
      return {
        kind: 'failure',
        title: 'Check your internet connection',
        message: 'The next Intelligent Navigator step cannot load because your internet connection appears to be unavailable.',
        guidanceHeading: safeGuidanceHeading,
        guidancePoints: [...reconnectFromNhsAppGuidancePoints],
      };
    }

    if (error instanceof BootstrapGatewayError && error.code === 'session-required') {
      return {
        kind: 'failure',
        title: 'Start again from the NHS App',
        message: 'We could not confirm your NHS App session, so Intelligent Navigator cannot continue safely from this page.',
        guidanceHeading: safeGuidanceHeading,
        guidancePoints: [...restartFromNhsAppGuidancePoints],
      };
    }

    if (error instanceof BootstrapGatewayError && error.code === 'retry-exhausted') {
      return {
        kind: 'failure',
        title: 'Intelligent Navigator cannot continue',
        message: 'There is a problem loading the information needed to continue this journey right now.',
        guidanceHeading: safeGuidanceHeading,
        guidancePoints: [...reconnectFromNhsAppGuidancePoints],
      };
    }

    return {
      kind: 'failure',
      title: 'Intelligent Navigator cannot continue',
      message: 'There is a problem loading the information needed to continue this journey right now.',
      guidanceHeading: safeGuidanceHeading,
      guidancePoints: [...reconnectFromNhsAppGuidancePoints],
    };
  }
}